<template>
    <div class="w-100 h-100 tracker-widget position-relative">
        <div v-if="tracker">
            <img
                src="../../../images/trackers/tracker-save.png"
                dusk="toggle-tracker-notes"
                @click="toggleNotesDialog()"
                alt="Save Tracker Data"
                class="position-absolute cursor-pointer save-button"
                v-tooltip.bottom="'Save Tracker Data'"
                style="bottom:0;right:0;z-index:2;"
                v-show="!draft"
            />
            <div
                v-if="notes"
                class="widget-note position-absolute d-flex flex-column justify-content-center align-items-center p-3"
            >
                <div
                    v-if="isFlaggable && !flagData"
                    class="w-100 h-100 d-flex flex-column justify-content-center align-items-center"
                >
                    <p class="text-justify">
                        Are you sure you want to save this data? You have only completed
                        {{ flagCompletedAttempts }} out of {{ flagExpectedAttempts }} trials.
                    </p>
                    <a
                        class="btn btn-sm btn-primary text-uppercase font-weight-bold text-white"
                        @click="flagData = true"
                        >Yes, Save and Flag Data</a
                    >
                    <a class="cursor-pointer link mt-1" @click="toggleNotesDialog()">> Go back</a>
                </div>
                <div v-else="" class="w-100 h-100 position-relative">
                    <div class="w-100 mb-3 note-container">
                        <textarea
                            id="notesData"
                            class="form-control h-100"
                            v-model="notesData"
                            placeholder="Notes"
                            dusk="note"
                        ></textarea>
                    </div>
                    <div class="d-flex w-100 mt-auto">
                        <a
                            class="btn btn-sm btn-primary text-uppercase font-weight-bold text-white"
                            @click="saveTrackerData()"
                            dusk="save-tracker-data"
                            >Save Tracking</a
                        >
                        <a class="cursor-pointer link ml-3" @click="toggleNotesDialog">> Go back</a>
                    </div>
                </div>
            </div>
            <div
                v-if="saving"
                class="widget-overlay position-absolute d-flex flex-column justify-content-center align-items-center"
            >
                <i class="fa fa-2x fa-spin fa-circle-o-notch"></i>
                <em>Saving...</em>
            </div>
            <div
                v-if="saved"
                class="widget-overlay position-absolute d-flex flex-column justify-content-center align-items-center"
            >
                <i class="fa fa-2x fa-check-circle-o text-success"></i>
                <em>Tracker data has been saved</em>
            </div>
        </div>

        <div
            class="bg-white h-100 px-2 pt-2 d-flex flex-column justify-content-start"
            style="background-color:#cdcdcd; border-radius: 0 0 6px 6px;"
        >
            <div class="d-flex justify-content-between align-center">
                <p class="mb-0">{{ goal ? goal.title.substring(0, 45) : "Goal Title" }}</p>
                <span class="ml-auto">
                    <a class="text-dark cursor-pointer" @click="$emit('goal-detail', goal.id)">
                        <i class="fa fa-line-chart mr-2"></i>
                    </a>
                    <a class="text-dark cursor-pointer" @click="closeTracker()">
                        <i class="fa fa-times mr-2"></i>
                    </a>
                </span>
            </div>
            <hr style="width:100%;margin: 0 auto;color:#c3c3c3;background: #c3c3c3; height: 1px;" />
            <p class="goal-properties text-uppercase">
                {{ goal ? goal.domain.name : "Domain" }} |
                {{ goal.tracker_type.replace("_", " ") }} | Goal: {{ goal ? goalSuccess : "N/A " }}
                {{
                    [
                        "cme",
                        "cold_probe",
                        "duration",
                        "tally",
                        "multi_tally",
                        "preference_assessment",
                        "time_sampling",
                    ].includes(goal.tracker_type)
                        ? ""
                        : "%"
                }}
            </p>
        </div>

        <div v-show="draft">
            <local-data
                :draft="draft"
                @retry-save="checkIfColdProbeAndRetrySave()"
                @erase-draft="eraseDraft()"
            >
                <template slot="info"
                    >This tracker has unsaved data from a previous session and can't be used before
                    the data is saved or discarded.</template
                >
                <template slot="action"
                    >See details</template
                >
            </local-data>
        </div>

        <div class="px-2" v-show="!draft">
            <component
                ref="tracker"
                :is="goal.tracker_type.replace('_', '-')"
                :goal="goal"
                :date="date"
                :notes-data="notesData"
                :flag-data="flagData"
                :customization="customization"
                @saved="finishedSaving($event)"
                @save-draft="saveDraft($event.error, $event.data, $event.endpoint)"
                @close-tracker="$emit('close-tracker', $event)"
            ></component>
        </div>

        <div class="mt-auto pb-5" v-if="!['cme'].includes(goal.tracker_type)" v-show="!draft">
            <p
                class="position-absolute link cursor-pointer pl-2"
                @click="undoAttempt()"
                style="bottom:0;left:0;z-index:2;"
            >
                > Oops, delete last entry
            </p>
        </div>
    </div>
</template>

<script>
import Articulation from "./types/Articulation";
import Cme from "./types/Cme";
import ColdProbe from "./types/ColdProbe";
import Duration from "./types/Duration";
import Mand from "./types/Mand";
import MandEchoic from "./types/MandEchoic";
import MultiPercent from "./types/MultiPercent";
import MultiTally from "./types/MultiTally";
import Percent from "./types/Percent";
import PreferenceAssessment from "./types/PreferenceAssessment";
import Tally from "./types/Tally";
import TaskAnalysis from "./types/TaskAnalysis";
import TimeSampling from "./types/TimeSampling";
import { saveDraft } from "../../plugins/saveDraft";

export default {
    name: "TrackerLive",
    props: ["goal", "date", "customization"],
    mixins: [saveDraft],
    components: {
        Articulation,
        Cme,
        ColdProbe,
        Duration,
        Mand,
        MandEchoic,
        MultiPercent,
        MultiTally,
        Percent,
        PreferenceAssessment,
        Tally,
        TaskAnalysis,
        TimeSampling,
    },

    data() {
        return {
            tracker: null,
            flagData: false,
            notes: false,
            notesData: "",
            saving: false,
            saved: false,
        };
    },

    mounted() {
        if (this.$refs.tracker) this.tracker = this.$refs.tracker;
        this.setEndpoint(
            "POST",
            `/api/patients/${this.goal.patient.id}/goals/${this.goal.id}/trackers`
        );
    },

    watch: {
        notes() {
            if (this.notes) {
                window.onbeforeunload = () => {
                    return true;
                };
            }
        },
    },

    computed: {
        flagCompletedAttempts() {
            let attempts = 0;
            switch (this.goal.tracker_type) {
                case "mand_echoic":
                    this.tracker.results.forEach(result =>
                        Array.isArray(result.results) &&
                        result.results.length === this.goal.parameters.echoic_repetitions
                            ? (attempts += 1)
                            : null
                    );
                    return attempts;
                case "multi_percent":
                    Object.keys(this.tracker.results).forEach(key =>
                        Array.isArray(this.tracker.results[key])
                            ? (attempts += this.tracker.results[key].length)
                            : null
                    );
                    return attempts;
                default:
                    return this.tracker.results.length;
            }
        },

        flagExpectedAttempts() {
            switch (this.goal.tracker_type) {
                case "mand":
                case "mand_echoic":
                    return this.goal.parameters.mand_opportunities;
                case "multi_percent":
                    return this.goal.parameters.targets.length * this.goal.parameters.attempts;
                default:
                    return this.goal.parameters.length;
            }
        },

        goalEndpoint() {
            return {
                method: "PATCH",
                url: `/api/patients/${this.goal.patient.id}/goals/${this.goal.id}`,
            };
        },

        goalSuccess() {
            switch (this.goal.tracker_type) {
                case "multi_percent":
                    return this.goal.parameters.attempts
                        ? parseInt(
                            (100 * this.goal.parameters.success) / this.goal.parameters.attempts
                        )
                        : this.goal.parameters.percent;
                case "percent":
                    return this.goal.parameters.attempts
                        ? parseInt(
                              (100 * this.goal.parameters.success) / this.goal.parameters.attempts
                          )
                        : this.goal.parameters.success;
                default:
                    return this.goal.parameters.success;
            }
        },

        isFlaggable() {
            if (!this.goal || !this.tracker) {
                return false; //tracker not yet loaded
            }
            if (
                ![
                    "percent",
                    "multi_percent",
                    "mand",
                    "mand_echoic",
                    "articulation",
                    "time_sampling",
                ].includes(this.goal.tracker_type)
            ) {
                return false; //non-flaggable goals
            }
            switch (this.goal.tracker_type) {
                case "percent":
                    return (
                        this.goal.parameters.attempts &&
                        this.tracker.results.length < this.goal.parameters.attempts
                    );
                case "multi_percent":
                    if (this.goal.parameters.attempts) {
                        let isMissingData = false;
                        Object.keys(this.tracker.results).forEach(target => {
                            isMissingData =
                                this.tracker.results[target].length < this.goal.parameters.attempts;
                        });
                        return isMissingData;
                    }
                    return false;
                case "mand":
                case "mand_echoic":
                    return this.tracker.results.length < this.goal.parameters.mand_opportunities;
                case "articulation":
                    return this.tracker.results.length < this.goal.parameters.targets.length;
                case "time_sampling":
                    return (
                        this.tracker.results.filter(r => !isNaN(r)).length <
                        parseInt(this.goal.parameters.intervals)
                    );
            }
        },
    },

    methods: {
        undoAttempt() {
            this.$refs.tracker.undoAttempt();
        },

        toggleNotesDialog() {
            this.flagData = false;
            this.notes = !this.notes;
        },

        saveTrackerData() {
            this.notes = false;
            this.saving = true;
            this.setEndpoint(
                "POST",
                `/api/patients/${this.goal.patient.id}/goals/${this.goal.id}/trackers`
            );
            this.$refs.tracker.saveTrackerData();
        },

        finishedSaving(success) {
            if (success) {
                this.saving = false;
                this.saved = true;
            } else {
                this.saving = false;
                this.saved = false;
            }
        },

        checkIfColdProbeAndRetrySave() {
            if (["cold_probe"].includes(this.goal.tracker_type)) {
                this.retrySave(this.goalEndpoint);
            } else {
                this.retrySave();
            }
        },

        closeTracker() {
            if (!this.saved) {
                if (
                    confirm("Choosing 'OK' will close this tracker without saving data. Continue?")
                ) {
                    this.$emit("close-tracker");
                }
            } else {
                this.$emit(
                    "close-tracker",
                    this.goal.title +
                        ": " +
                        `Scored ${this.$refs.tracker.successRate}%` +
                        " - " +
                        this.$refs.tracker.notesData
                );
            }
        },
    },
};
</script>
